// Ez a komponens a naplózott edzések megjelenítéséért felelős, localStorage segítségével tárolja az adatokat.
import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardMedia, CardContent, Chip, Grid, IconButton, Paper } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import { Exercise } from '../utils/types';  // Az Exercise típus importálása



const LoggedExercises: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs()); // A kiválasztott dátum, alapértelmezetten a mai dátum
  const [loggedExercises, setLoggedExercises] = useState<Exercise[]>([]); // A naplózott gyakorlatok tárolása

  // A naplózott gyakorlatok lekérése a localStorage-ból a kiválasztott dátum alapján
  useEffect(() => {
    const fetchLoggedExercises = () => {
      if (selectedDate) {
        const dateKey = selectedDate.format('YYYY-MM-DD'); // A kiválasztott dátum formázása stringgé
        const storedExercises = JSON.parse(localStorage.getItem(dateKey) || '[]'); // A localStorage-ból a naplózott gyakorlatok lekérése
        setLoggedExercises(storedExercises); // A naplózott gyakorlatok beállítása a state-be
      }
    };

    fetchLoggedExercises(); // A naplózott gyakorlatok lekérése a komponens frissítésekor
  }, [selectedDate]); // A useEffect csak akkor fut le, ha a selectedDate állapot változik

  // Funkció a gyakorlat törlésére a naplózott gyakorlatok közül
  const removeExercise = (index: number) => {
    if (selectedDate) {
      // A naplózott gyakorlatok tömbjéből az adott indexű elem törlése
      const dateKey = selectedDate.format('YYYY-MM-DD');
      const updatedExercises = [...loggedExercises]; // A naplózott gyakorlatok másolata
      updatedExercises.splice(index, 1); // Az adott indexű elem törlése

      localStorage.setItem(dateKey, JSON.stringify(updatedExercises)); // A naplózott gyakorlatok frissítése a localStorage-ban
      setLoggedExercises(updatedExercises); // A state frissítése a törölt listával
    }
  };

  return (
    <Box sx={{ px: 3, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* A Paper egy izléses material UI elem, a cím megjelenítésére */}
      <Paper
        elevation={4}
        sx={{
          p: 2,
          backgroundColor: '#f0f4f8',
          borderRadius: 2,
          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
          mb: 2,
          textAlign: 'center',
          width: '100%',
          maxWidth: 600,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'black' }}>
          Logged Exercises
        </Typography>
        <Box sx={{ margin: "10px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              sx={{ width: 250 }}
            />
          </LocalizationProvider>
        </Box>
        <Typography variant="h6" sx={{ color: '#1565c0', mt: 1 }}>
          Exercises on {selectedDate?.format('YYYY-MM-DD')}
        </Typography>
      </Paper>

      <Box sx={{ mt: 2, width: '100%' }}>
        <Grid container spacing={2} justifyContent="flex-start">
          {/* Ha nincs naplózott gyakorlat, akkor egy üzenet jelenik meg */}
          {loggedExercises.length === 0 ? (
            <Typography sx={{ color: '#555', textAlign: 'center', width: '100%', margin: "20px" }}>
              No exercises logged for this day.
            </Typography>
          ) : (
            /* A naplózott gyakorlatok megjelenítése */
            loggedExercises.map((exercise: Exercise, index: number) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
                <Card
                  sx={{
                    height: 350,
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.05)' },
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    borderRadius: 2,
                  }}
                >
                  <CardMedia
                    component="img"
                    image={exercise.gifUrl}
                    alt={exercise.name}
                    sx={{
                      height: 200,
                      objectFit: 'contain',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }}
                  />
                  <CardContent>
                    <Typography variant="h6" component="div" sx={{ textAlign: 'center', mb: 1 }}>
                      {exercise.name}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Chip
                        label={exercise.bodyPart}
                        variant="outlined"
                        sx={{
                          color: '#1565c0',
                          borderColor: '#1565c0',
                          borderRadius: '16px',
                          fontSize: '14px',
                        }}
                      />
                      <Chip
                        label={exercise.target}
                        variant="outlined"
                        sx={{
                          color: '#1565c0',
                          borderColor: '#1565c0',
                          borderRadius: '16px',
                          fontSize: '14px',
                        }}
                      />
                    </Box>
                  </CardContent>
                  {/* A törlés gomb a gyakorlat törléséhez */}
                  <IconButton
                    aria-label="delete"
                    onClick={() => removeExercise(index)}
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      right: 10,
                      color: 'red',
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default LoggedExercises;
